/**
 * /locations — polished directory of the five VidaVital clinics.
 *
 * Source of truth: `../data/clinics.js` (do not duplicate NAP inline).
 * Rendered content is deliberately factual: name, address, phone (local),
 * modality (in-clinic + telehealth-state), hours, metro. No efficacy claims,
 * no appointment promises, no invented availability.
 */
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, ArrowRight, Video } from "lucide-react";
import { trackPhoneCall } from "../lib/analytics";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
// ClinicalReviewByline belongs on editorial article pages, not location directories.
import { CLINICS } from "../data/clinics";

const ORIGIN = "https://medicalpeptides.ai";

function buildDirectorySchema() {
  const items = CLINICS.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${ORIGIN}/locations/${c.slug}`,
    name: c.display_name,
  }));
  return [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: items,
    },
    ...CLINICS.map((c) => ({
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      name: c.display_name,
      url: `${ORIGIN}/locations/${c.slug}`,
      telephone: c.phone_e164,
      email: c.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: c.street,
        addressLocality: c.city,
        addressRegion: c.state,
        postalCode: c.zip,
        addressCountry: "US",
      },
      areaServed: { "@type": "State", name: c.state_full },
      openingHoursSpecification: c.hours_schema,
      parentOrganization: { "@type": "MedicalOrganization", name: "VidaVital Medical", url: ORIGIN },
    })),
  ];
}

export default function LocationsDirectory() {
  const schema = buildDirectorySchema();
  return (
    <div data-testid="locations-directory">
      <SEO
        title="VidaVital Medical Clinic Locations | MedicalPeptides.ai"
        description="Five VidaVital Medical clinics serving Las Vegas NV, Albuquerque NM, Coral Gables FL, Brookfield WI, and Omaha NE. In-clinic evaluations plus state-licensed telehealth where clinically appropriate."
        path="/locations"
        schema={schema}
        breadcrumbs={[{ name: "Locations", path: "/locations" }]}
      />

      <section className="vv-hero">
        <div className="vv-container py-14 md:py-16">
          <Breadcrumbs items={[{ name: "Locations", path: "/locations" }]} />
          <div className="vv-hero-eyebrow mb-2 mt-2">Clinic Locations</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight">VidaVital Medical clinics</h1>
          <p className="mt-4 text-stone-600 max-w-2xl">
            Five clinics across the U.S. offering in-person provider-led peptide-therapy consultations, plus secure-video telehealth for patients in the state where each clinic is licensed. Every patient starts with a licensed medical provider evaluation &mdash; treatment recommendations are individualized and provided only when clinically appropriate.
          </p>
        </div>
      </section>

      <section className="vv-container py-8 md:py-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CLINICS.map((c) => (
          <article
            key={c.slug}
            data-testid={`location-card-${c.slug}`}
            className="vv-card p-6 flex flex-col"
          >
            <div className="flex items-center gap-2 text-[color:var(--vv-teal)]">
              <MapPin size={18} />
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">{c.state_full}</span>
            </div>
            <h2 className="font-display text-2xl mt-2 leading-tight">
              <Link to={`/locations/${c.slug}`} data-testid={`location-title-${c.slug}`} className="hover:text-[color:var(--vv-teal)]">
                {c.name}, {c.state}
              </Link>
            </h2>
            <div className="text-sm text-stone-500 mt-1">{c.metro}</div>

            <div className="mt-4 pt-4 border-t border-stone-100 text-sm text-stone-700 leading-relaxed">
              <div>{c.address_line_1}</div>
              <div>{c.address_line_2}</div>
              <div>{c.city}, {c.state} {c.zip}</div>
            </div>

            <div className="mt-3 text-xs text-stone-500 flex items-start gap-1.5">
              <Clock size={12} className="mt-0.5 flex-shrink-0" />
              <span>{c.hours}</span>
            </div>
            <div className="mt-1 text-xs text-stone-500 flex items-start gap-1.5">
              <Video size={12} className="mt-0.5 flex-shrink-0" />
              <span data-testid={`location-services-${c.slug}`}>{c.services_summary}</span>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <a
                href={`tel:${c.phone_e164}`}
                onClick={() => trackPhoneCall(`directory_${c.slug}`)}
                aria-label={`Call VidaVital Medical ${c.name} at ${c.phone_display}`}
                data-testid={`location-phone-${c.slug}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--vv-teal)] hover:text-[color:var(--vv-teal-hover)]"
              >
                <Phone size={14} /> {c.phone_display}
              </a>
              <Link
                to={`/locations/${c.slug}`}
                data-testid={`location-view-${c.slug}`}
                className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-stone-600 hover:text-[color:var(--vv-ink)]"
              >
                View clinic <ArrowRight size={12} />
              </Link>
            </div>
          </article>
        ))}
      </section>

      <section className="vv-container pb-16 text-xs text-stone-500 leading-relaxed max-w-3xl">
        <p>
          <strong>Please note:</strong> Treatment recommendations are individualized. Every patient is evaluated by a licensed medical provider. Eligibility depends on medical history, provider assessment, and applicable state and federal laws. Peptide therapies are provided only when clinically appropriate. Information on this page is educational and does not replace personalized medical advice, diagnosis, or treatment.
        </p>
      </section>
    </div>
  );
}
